import { useCallback, useState } from "react";
import { Outlet, useLocation, useParams, useSearchParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { TabNav } from "@/components/TabNav";
import { hasRole } from "@/lib/hasRole";
import { plural } from "@/lib/plural";

const tabs = [
  { label: "Gateways", to: "gateways", entityLabel: "gateway" },
  { label: "Connectors", to: "connectors", entityLabel: "connector" },
];

export interface NetworkingSummary {
  total: number;
  offline: number;
  entityLabel: string;
}

export interface NetworkingLayoutContext {
  setSummary: (summary: NetworkingSummary | null) => void;
  openCreate: () => void;
}

export function ConnectorsAndGatewaysLayout() {
  const { user } = useAuth();
  const { orgId } = useParams();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<NetworkingSummary | null>(null);

  const activeTab =
    tabs.find((tab) => location.pathname.endsWith(`/${tab.to}`)) ?? tabs[0]!;

  const openCreate = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("create", "1");

        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return (
    <div className="text-foreground">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Networking</h1>
          {summary && (
            <p className="mt-0.5 text-sm text-foreground/70">
              {plural(summary.total, summary.entityLabel)}
              {summary.offline > 0 && ` · ${summary.offline} offline`}
            </p>
          )}
        </div>
        {hasRole(user?.roles, "Admin") && (
          <Button variant="primary" onClick={openCreate}>
            Create {activeTab.entityLabel}
          </Button>
        )}
      </div>

      <TabNav tabs={tabs} basePath={`/${orgId}/networking`} />

      <Outlet
        context={{ setSummary, openCreate } satisfies NetworkingLayoutContext}
      />
    </div>
  );
}
